import * as React from 'react'

import {
  Controller,
  UseFormReturn,
  useController,
  useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  ErrorText,
  Grid,
  Label,
  RadioButton,
  Select,
  SelectOptionInterface,
  Stack,
  Text,
} from '@enechain/ecloud-designsystem'
import { DeliveryType } from '@enechain/eposition-proto/types/v1/deal_pb'
import { Card } from '@enechain/eposition-ui'

import { useSimulationOptions } from '~/pages/Simulation/hooks/useSimulationOptions'
import { useSyncOutline } from '~/pages/Simulation/hooks/useSyncOutline'
import { OrderSimulationSchema } from '~/pages/Simulation/schemas'

type Props = {
  formMethods: UseFormReturn<OrderSimulationSchema>
  index: number
}

export const TransactionOutlineForm: React.FC<Props> = ({
  formMethods,
  index,
}) => {
  const { t } = useTranslation(['domain', 'common'])
  const {
    dealPositions,
    isPhysicalFutureTradeOptions,
    futureDeliveryTypes,
    physicalDeliveryTypes,
    deliveryTypeLabels,
  } = useSimulationOptions()

  const { field: dealPositionField } = useController({
    name: `orderPattern.${index}.dealPosition`,
    control: formMethods.control,
  })
  const { handleChangePhysicalFuture } = useSyncOutline({
    formMethods,
    index,
  })
  const isPhysicalFuture = useWatch({
    control: formMethods.control,
    name: `orderPattern.${index}.isPhysicalFuture`,
  })
  const selfDeliveryType = useWatch({
    control: formMethods.control,
    name: `orderPattern.${index}.selfDeliveryType`,
  })
  const deliveryTypes: Array<{
    label: string
    value: DeliveryType
  }> = isPhysicalFuture ? futureDeliveryTypes : physicalDeliveryTypes

  return (
    <Card>
      <Grid gridGap="4" gridTemplateColumns="1fr 3fr">
        <Label required>
          {t('domain:model.simulation.column.deal_position')}
        </Label>
        <RadioButton
          onValueChange={({ value }): void => {
            dealPositionField.onChange(Number(value))
          }}
          value={dealPositionField.value.toString()}
          radios={dealPositions.map((item) => ({
            label: item.label,
            value: item.value.toString(),
          }))}
        />
        <Label required>
          {t('domain:model.simulation.column.physical_future_trade')}
        </Label>
        <Controller
          render={({ field: { value, onChange } }) => (
            <RadioButton
              onValueChange={({ value }): void => {
                const newValue = value === 'true'
                onChange(newValue)
                handleChangePhysicalFuture(newValue, index)
              }}
              value={value === true ? 'true' : 'false'}
              radios={isPhysicalFutureTradeOptions}
            />
          )}
          name={`orderPattern.${index}.isPhysicalFuture`}
          control={formMethods.control}
        />
        <Label required>
          {t('domain:model.simulation.column.self_delivery_type')}
        </Label>
        {isPhysicalFuture ? (
          <Text textStyle="textMd">{deliveryTypeLabels[selfDeliveryType]}</Text>
        ) : (
          <Controller
            render={({ field: { value, onChange } }) => (
              <Stack gap="1">
                <Select<
                  {
                    label: string
                    value: DeliveryType
                  } & SelectOptionInterface,
                  false
                >
                  isMulti={false}
                  getOptionValue={(option) => option.value.toString()}
                  onChange={(item): void => {
                    onChange(item?.value ?? DeliveryType.UNSPECIFIED)
                  }}
                  options={deliveryTypes}
                  selectedOptions={
                    deliveryTypes.find((item) => item.value === value) ?? {
                      label: t('common:form.label.unspecified'),
                      value: DeliveryType.UNSPECIFIED,
                    }
                  }
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                {formMethods.formState.errors?.orderPattern?.[index]
                  ?.selfDeliveryType?.message !== undefined && (
                  <ErrorText>
                    {
                      formMethods.formState.errors?.orderPattern?.[index]
                        ?.selfDeliveryType?.message
                    }
                  </ErrorText>
                )}
              </Stack>
            )}
            name={`orderPattern.${index}.selfDeliveryType`}
            control={formMethods.control}
          />
        )}
        {isPhysicalFuture && (
          <>
            <Label required>
              {t('domain:model.simulation.column.counterparty_delivery_type')}
            </Label>
            <Controller
              render={({ field: { value, onChange } }) => (
                <Stack gap="1">
                  <Select<
                    {
                      label: string
                      value: DeliveryType
                    } & SelectOptionInterface,
                    false
                  >
                    isMulti={false}
                    getOptionValue={(option) => option.value.toString()}
                    onChange={(item): void => {
                      onChange(item?.value ?? DeliveryType.UNSPECIFIED)
                    }}
                    options={deliveryTypes}
                    selectedOptions={
                      deliveryTypes.find((item) => item.value === value) ?? {
                        label: t('common:form.label.unspecified'),
                        value: DeliveryType.UNSPECIFIED,
                      }
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                  {formMethods.formState.errors?.orderPattern?.[index]
                    ?.eClearDeliveryType?.message !== undefined && (
                    <ErrorText>
                      {
                        formMethods.formState.errors?.orderPattern?.[index]
                          ?.eClearDeliveryType?.message
                      }
                    </ErrorText>
                  )}
                </Stack>
              )}
              name={`orderPattern.${index}.eClearDeliveryType`}
              control={formMethods.control}
            />
          </>
        )}
      </Grid>
    </Card>
  )
}
